import React, { useState, useEffect, useCallback } from "react"
import { connect } from "getstream"

const NOTIFICATION_ICONS = {
  like: "\u2764\ufe0f",
  follow: "\ud83d\udc64",
  comment: "\ud83d\udcac",
  purchase: "\ud83d\udcb0",
  mention: "@",
  system: "\u2139\ufe0f",
  default: "\u2022",
}

const NotificationFeed = ({ userId, apiKey, appId, token, onUnreadCount }) => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [feed, setFeed] = useState(null)

  useEffect(() => {
    if (!apiKey || !appId || !token || !userId) {
      setError("Missing required configuration")
      setLoading(false)
      return
    }

    let isActive = true

    const init = async () => {
      try {
        const client = connect(apiKey, null, appId)
        const notifFeed = client.feed("notification", userId, token)

        if (!isActive) return
        setFeed(notifFeed)

        const response = await notifFeed.get({ limit: 50, mark_read: false, mark_seen: false })
        if (!isActive) return

        const results = response.results || []
        setNotifications(results)
        const unread = results.filter(n => !n.is_read).length
        setUnreadCount(unread)
        if (onUnreadCount) onUnreadCount(unread)
        setLoading(false)
      } catch (err) {
        console.error("NotificationFeed: Failed to fetch:", err)
        if (isActive) {
          setError(`Failed to load notifications: ${err.message}`)
          setLoading(false)
        }
      }
    }

    init()
    return () => { isActive = false }
  }, [apiKey, appId, token, userId, onUnreadCount])

  const markAsRead = useCallback(async (notificationId) => {
    if (!feed) return
    try {
      await feed.get({ mark_read: [notificationId] })
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      ))
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1)
        if (onUnreadCount) onUnreadCount(next)
        return next
      })
    } catch (err) {
      console.error("Failed to mark notification as read:", err)
    }
  }, [feed, onUnreadCount])

  const markAllAsRead = useCallback(async () => {
    if (!feed) return
    try {
      await feed.get({ mark_read: true })
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
      if (onUnreadCount) onUnreadCount(0)
    } catch (err) {
      console.error("Failed to mark all as read:", err)
    }
  }, [feed, onUnreadCount])

  const formatTime = (date) => {
    const diff = Date.now() - new Date(date)
    const mins = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)
    if (mins < 1) return "just now"
    if (mins < 60) return `${mins}m ago`
    if (hours < 24) return `${hours}h ago`
    return `${days}d ago`
  }

  const getIcon = (group) => NOTIFICATION_ICONS[group] || NOTIFICATION_ICONS.default

  const summarize = (notification) => {
    const acts = notification.activities || []
    if (acts.length === 0) return "New notification"
    if (acts.length === 1) {
      const a = acts[0]
      return `${a.actor || "Someone"} ${a.verb || "interacted with"} ${a.object || "something"}`
    }
    const first = acts[0].actor || "Someone"
    return `${first} and ${acts.length - 1} other${acts.length > 2 ? "s" : ""} ${acts[0].verb || "interacted"}`
  }

  if (error) {
    return <div className="cf-notif-error"><p>{error}</p></div>
  }

  if (loading) {
    return (
      <div className="cf-notif-loading">
        <div className="cf-notif-loading__spinner" />
        <p>Loading notifications...</p>
      </div>
    )
  }

  return (
    <div className="cf-notification-feed">
      {unreadCount > 0 && (
        <div className="cf-notif-header">
          <button className="cf-notif-mark-all" onClick={markAllAsRead}>Mark all read</button>
        </div>
      )}

      <div className="cf-notif-list">
        {notifications.length === 0 ? (
          <div className="cf-notif-empty">No notifications</div>
        ) : (
          notifications.map(n => (
            <div
              key={n.id}
              className={`cf-notif-item ${!n.is_read ? "cf-notif-item--unread" : ""}`}
              onClick={() => !n.is_read && markAsRead(n.id)}
            >
              <div className="cf-notif-item__icon">{getIcon(n.group)}</div>
              <div className="cf-notif-item__body">
                <div className="cf-notif-item__text">{summarize(n)}</div>
                <div className="cf-notif-item__time">{formatTime(n.created_at)}</div>
              </div>
              {!n.is_read && <div className="cf-notif-item__dot" />}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default NotificationFeed
